import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useChatContext } from '../../context/ChatContext';
import { useAuthContext } from '../../context/AuthContext';
import ChatList from '../Chat/ChatList';
import ChatWindow from '../Chat/ChatWindow';
import ChatHeaderBar from '../Chat/ChatHeaderBar';
import ChatSearchBar from '../Chat/ChatSearchBar';
import CreateGroupModal from '../Groups/CreateGroupModal';
import ClearChatModal from '../Chat/ClearChatModal';
import useUnifiedChats from '../../hooks/useUnifiedChats';
import { getSocket, joinChat, joinChatRoom, leaveChatRoom } from '../../socket';

const ChatController = ({ isOpen, setIsOpen, activeTab, setActiveTab }) => {
    const {
        activeChat,
        openChat,
        pinnedIds,
        drafts,
        updateDraft
    } = useChatContext();

    const { user } = useAuthContext();
    const currentUserId = useMemo(() =>
        String(user?._id || user?.userId || ''),
        [user]);

    const [replyTo, setReplyTo] = useState(null);
    const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);
    const [chatToClear, setChatToClear] = useState(null);

    const {
        chats,
        searchTerm,
        setSearchTerm,
        selectedChatId,
        messagesByChatId,
        handleSendMessage,
        handleTyping,
        handleLoadMore,
        handleReactToMessage,
        hasMoreByChatId,
        unreadMap,
        typingByChatId,
        markMessagesSeen,
        handleChatAction,
    } = useUnifiedChats();

    const currentChat = activeChat;
    const chatId = useMemo(() =>
        currentChat?._id ? String(currentChat._id) : null,
        [currentChat?._id]);

    const currentDraft = chatId ? (drafts[chatId] || '') : '';

    // ---------------------------------------------------------
    // 📡 1. SOCKET: Identity (Mailbox)
    // ---------------------------------------------------------
    useEffect(() => {
        const socket = getSocket();
        if (socket && currentUserId) {
            console.log('🆔 [ChatController] Registering Identity:', currentUserId);
            if (socket.connected) joinChat(currentUserId);

            const handleOnConnect = () => {
                console.log('🟢 [ChatController] Socket reconnected, re-joining personal room.');
                joinChat(currentUserId);
            };

            socket.on('connect', handleOnConnect);
            return () => socket.off('connect', handleOnConnect);
        }
    }, [currentUserId]);

    // ---------------------------------------------------------
    // 📡 2. SOCKET: Conversation Room (Live Messaging)
    // ---------------------------------------------------------
    useEffect(() => {
        const socket = getSocket();
        if (socket && chatId) {
            console.log(`🔌 [ChatController] Entering Chat Room: ${chatId}`);
            joinChatRoom(chatId);

            return () => {
                console.log(`🔌 [ChatController] Leaving Chat Room: ${chatId}`);
                leaveChatRoom(chatId);
            };
        }
    }, [chatId]);

    // ---------------------------------------------------------
    // 🔍 UI DIAGNOSTICS
    // ---------------------------------------------------------
    useEffect(() => {
        if (isOpen) {
            console.log('🖼️ [ChatController] Widget Mounted/Opened:', {
                userId: currentUserId,
                chatId: chatId || 'NONE (LIST VIEW)',
                tab: activeTab
            });
        }
    }, [isOpen, chatId, activeTab, currentUserId]);


    // Clear reply state when chat changes
    useEffect(() => {
        setReplyTo(null);
    }, [chatId]);

    const onSend = useCallback(
        (data) => {
            const isObject = typeof data === 'object' && data !== null;
            const messageText = isObject ? data.content : data;
            const targetChatId = isObject ? (data.chatId || chatId) : chatId;
            const targetReplyTo = isObject ? (data.replyTo || replyTo?._id) : replyTo?._id;

            if (!targetChatId || !handleSendMessage) return;

            const finalContent = typeof messageText === 'string' ? messageText.trim() : '';
            if (!finalContent) return;

            handleSendMessage({
                chatId: String(targetChatId),
                content: finalContent,
                replyTo: targetReplyTo || null,
            });

            updateDraft(targetChatId, '');
            setReplyTo(null);
        },
        [chatId, replyTo, handleSendMessage, updateDraft]
    );

    const onChatAction = (chatId, action) => {
        if (action === 'clear') {
            setChatToClear(chatId);
            setShowClearModal(true);
        } else {
            handleChatAction(chatId, action);
        }
    };

    const confirmClear = () => {
        if (chatToClear) {
            handleChatAction(chatToClear, 'clear');
            setShowClearModal(false);
            setChatToClear(null);
        }
    };

    const handleSelectChat = useCallback((chatOrFriend) => {
        if (!chatOrFriend) return;
        if (chatOrFriend._id && !chatOrFriend.isFriendFallback) {
            openChat(chatOrFriend);
            return;
        }

        const friendId = String(chatOrFriend._id || chatOrFriend.id);
        const existingChat = chats.find(c =>
            (c.type === 'DM' || !c.isGroup) &&
            (c.members || c.participants || []).some(m => String(m._id || m) === friendId) &&
            !c.isFriendFallback
        );

        if (existingChat) {
            openChat(existingChat);
        } else {
            openChat(chatOrFriend);
        }
    }, [chats, openChat]);

    const handleBack = useCallback(() => {
        openChat(null);
        setReplyTo(null);
    }, [openChat]);

    const handleCloseWidget = useCallback(() => {
        setIsOpen(false);
        if (activeChat) {
            handleBack();
        }
    }, [setIsOpen, activeChat, handleBack]);

    return (
        <div className="chat-box" style={{ display: isOpen ? 'flex' : 'none' }}>
            <ChatHeaderBar
                chat={currentChat}
                currentUserId={currentUserId}
                onBack={currentChat ? handleBack : null}
                onClose={handleCloseWidget}
            />

            <div className="chat-panel">
                {!currentChat ? (
                    <div className="chat-list-container full-height full-width-layout">
                        <div className="chat-tabs-wrapper">
                            <div className="chat-tabs">
                                {['all', 'direct', 'group', 'unread', 'archived'].map((mode) => (
                                    <button
                                        key={mode}
                                        className={activeTab === mode ? 'active' : ''}
                                        onClick={() => setActiveTab(mode)}
                                    >
                                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                                    </button>
                                ))}
                            </div>

                            <button
                                className="create-new-btn"
                                onClick={() => setIsCreateGroupOpen(true)}
                                aria-label="Create New Group"
                            >
                                ＋
                            </button>
                        </div>

                        <ChatSearchBar value={searchTerm} onChange={setSearchTerm} />

                        <div className="chat-list-scroller">
                            <ChatList
                                items={chats}
                                currentTab={activeTab}
                                selectedId={selectedChatId}
                                onSelect={handleSelectChat}
                                onAction={onChatAction}
                                notifications={unreadMap}
                                messagesByChatId={messagesByChatId}
                                pinnedIds={pinnedIds}
                                currentUserId={currentUserId}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="chat-window-wrapper full-width-layout" style={{ width: '100%', height: '100%' }}>
                        <ChatWindow
                            chat={currentChat}
                            currentUserId={currentUserId}
                            messages={messagesByChatId?.[chatId] || []}
                            newMessage={currentDraft}
                            setNewMessage={(text) => updateDraft(chatId, text)}
                            onSendMessage={onSend}
                            onReactToMessage={handleReactToMessage}
                            handleTyping={handleTyping}
                            typingUsers={typingByChatId?.[chatId] || []}
                            onLoadMore={() => handleLoadMore(chatId)}
                            hasMore={hasMoreByChatId?.[chatId] ?? true}
                            onBack={handleBack}
                            markMessagesSeen={markMessagesSeen}
                        />
                    </div>
                )}
            </div>

            {isCreateGroupOpen && (
                <CreateGroupModal
                    onClose={() => setIsCreateGroupOpen(false)}
                />
            )}

            <ClearChatModal
                isOpen={showClearModal}
                chatName={chats.find(c => String(c._id) === String(chatToClear))?.chatName || "this user"}
                onConfirm={confirmClear}
                onCancel={() => setShowClearModal(false)}
            />
        </div>
    );
};

export default ChatController;